import React, { useEffect, Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  List,
  Divider,
  ListItem,
  Typography,
  IconButton,
  ListItemText,
  ListItemSecondaryAction,
} from '@material-ui/core';
import Paginator from 'components/Paginator';

import {
  useStyles,
  listItemsStyles,
  customCardStyles,
  creditsListTextStyles,
} from './Credits.styled';
import i18n from 'i18n-js';
import moment from 'moment';
import Can from 'components/Can';
import { Card } from 'components/UI';
import { formatAmount } from 'utils';
import Feature from 'components/Feature';
import { CustomersAction } from 'state/modules/customers';
import DeleteIcon from '@material-ui/icons/Delete';
import { AddCreditsModal } from 'components/Modals';
import Loading from 'components/Loading';

const CreditsWidget = ({ user }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const cardClasses = customCardStyles();
  const listItemsClasses = listItemsStyles();
  const creditlistItemsClasses = creditsListTextStyles();
  const [params, setParams] = useState({
    page: 1,
    limit: 3,
  });

  const account = useSelector((state) => state?.customers?.account);
  const credits = useSelector((state) => state?.customers?.credits);
  const creditsPagination = useSelector((state) => state?.customers?.creditsPagination);
  const fetchingCredit = useSelector((state) => state?.customers?.fetchingCredit);
  const fetchingRevokeCredit = useSelector((state) => state?.customers?.fetchingRevokeCredit);

  const loading = isNaN(user?.account_id) || fetchingCredit || fetchingRevokeCredit;

  const [openCreditModal, setOpenCreditModal] = useState(false);
  const { localeFormat } = useSelector((state) => state.organizations);

  /**
   * Handle open edit account modal
   */
  const handleModalOpen = (type) => {
    setOpenCreditModal((prevState) => !prevState);
  };

  /**
   * Handle revoke credit
   */
  const handleRevokeCredit = (id, account) => {
    dispatch(CustomersAction.fetchRevokeCredit(id, account));
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    {
      title: i18n.t('payments.add_credit'),
      onClick: () => handleModalOpen(),
      can: { action: 'create', name: 'credit' },
    },
  ];

  useEffect(() => {
    if (user?.account_id) {
      dispatch(CustomersAction.fetchAccountCreditById({ account_id: user.account_id, ...params }));
    }
  }, [user?.account_id, dispatch, params]);

  const handleChangePage = (event, newPage) => {
    const newParams = { ...params, page: newPage + 1 };
    setParams(newParams);
    dispatch(CustomersAction.fetchAccountCreditById({ account_id: user.account_id, ...params }));
  };

  return (
    <Card
      menuItems={items}
      headerActionType="vertical"
      title={i18n.t('common.credits')}
      contentRootClasses={cardClasses}
    >
      <Feature name="credit" is="enabled">
        <Can I="read" on="credit">
          <Box>
            {loading ? (
              <Loading />
            ) : credits?.length ? (
              <>
                <List component="nav" aria-labelledby="list" className={classes.creditsListRoot}>
                  {credits?.map((credit, index) => {
                    return (
                      <Fragment key={index}>
                        <ListItem>
                          <ListItemText>
                            <Box display="flex" flexDirection="column">
                              <Typography
                                color="textSecondary"
                                style={{ lineHeight: 1.2, marginRight: 89, marginBottom: 4 }}
                              >
                                <Box fontSize={13} component="span">
                                  {credit.display_name}
                                </Box>
                              </Typography>
                              <Typography
                                color="textSecondary"
                                style={{ lineHeight: 1.2, marginRight: 89, marginBottom: 16 }}
                              >
                                <Box fontSize={13} component="span">
                                  <span className={creditlistItemsClasses.expDate}>
                                    {i18n.t('common.expiration_date')}:
                                  </span>
                                  {` ${moment(credit.expiration).format(
                                    `${localeFormat} (UTC Z)`
                                  )}`}
                                </Box>
                              </Typography>
                              <Typography
                                color="textSecondary"
                                style={{ lineHeight: 1.2, marginRight: 89, marginBottom: 4 }}
                              >
                                <Box fontSize={13} component="span">
                                  <span className={creditlistItemsClasses.description}>
                                    {i18n.t('common.description')}:
                                  </span>
                                  {credit.description}
                                </Box>
                              </Typography>
                            </Box>
                          </ListItemText>
                          <ListItemSecondaryAction classes={listItemsClasses}>
                            <Box display="flex" alignItems="center">
                              <Box display="flex" flexDirection="column">
                                <Typography color="textSecondary" style={{ lineHeight: 1.2 }}>
                                  <Box
                                    fontSize={12}
                                    component="span"
                                    className={creditlistItemsClasses.primary}
                                  >
                                    $ {formatAmount(credit.amount_issued)}
                                  </Box>
                                </Typography>
                                <Typography style={{ lineHeight: 1.2 }}>
                                  <Box
                                    fontSize={10}
                                    component="span"
                                    className={creditlistItemsClasses.creditsUsed}
                                  >
                                    $ {formatAmount(credit.amount_consumed)}
                                  </Box>
                                </Typography>
                              </Box>
                              {credit.is_valid && (
                                <IconButton
                                  component="span"
                                  aria-label="Remove credit"
                                  disabled={fetchingRevokeCredit}
                                  onClick={() => handleRevokeCredit(credit.id, user.account_id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider className={classes.divider} />
                      </Fragment>
                    );
                  })}
                </List>
                <Paginator
                  component="div"
                  limit={params.limit}
                  pagination={creditsPagination}
                  onPageChange={handleChangePage}
                />
              </>
            ) : (
              <Typography color="textPrimary" style={{ lineHeight: 1 }}>
                <Box fontSize={14} component="span" lineHeight={1}>
                  {i18n.t('customers.no_credits')}
                </Box>
              </Typography>
            )}
          </Box>
        </Can>
      </Feature>

      {openCreditModal && (
        <AddCreditsModal
          to="driver"
          type="credit"
          user={user}
          account={account}
          accountLevel={true}
          open={openCreditModal}
          handleClick={() => handleModalOpen('credit')}
        />
      )}
    </Card>
  );
};

export { CreditsWidget };
